// "My Decks changed": the collection data that depends on them is no longer
// current. The smart binder spare rule (mtg-api binder spare rule) counts a
// card's real copies MINUS the copies its My Decks need, so a deck save,
// delete, ownership change, source refresh, restore or remote reconcile can
// move a card into or out of a spare binder with NO collection write. The
// copies write fence never sees that, so this module is the one signal.
//
// invalidateDeckDependentCollectionData() (call it after every successful
// My Decks change):
//  - bumps the deck GENERATION (read by the spare preview's query key, so a
//    spare preview cached before the change is never answered from cache,
//    and an older in-flight one lands under a key nobody reads);
//  - tells every registered query client (App.tsx registers its one), which
//    then applyDeckDependentInvalidation():
//      - every binders list holding a spare binder: in-flight reads are
//        cancelled, the spare binders' counts are dropped to "checking"
//        (null counts, stats_pending) and the list is re-read; if that
//        re-read fails they settle to UNAVAILABLE (stats_available false),
//        never back to a pre-change count (settlePending, generation-guarded);
//      - every copy search / grouped search / pocket read whose binder is a
//        spare binder (or any binder, when the binders list is not known),
//        or whose filters name spare: RESET (pages dropped, in-flight answer
//        cancelled, pagination restarts from page 1);
//      - every spare preview: removed.
//
// A plain module (no React), like builtDecksSignal, so useSavedDecks can call
// it without pulling the collection in.
import type { QueryClient, QueryKey } from '@tanstack/react-query'
import { rulesNameSpare, type Binder, type BindersList } from '../api/collectionBinders'
import { KEY_SEPARATOR, QK } from '../api/collectionCopies.codes'

let generation = 0
type Listener = () => void
const listeners = new Set<Listener>()

/** The current deck generation (bumped by every invalidation). */
export function deckGeneration(): number {
  return generation
}

/** Subscribe to deck changes; returns the unsubscribe (useSyncExternalStore-shaped). */
export function subscribeDeckGeneration(listener: Listener): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

/**
 * My Decks changed (a save, delete, ownership change, source refresh,
 * restore or remote reconcile landed). Bumps the deck generation and drops
 * every deck-dependent collection cache (see the module comment).
 */
export function invalidateDeckDependentCollectionData(): void {
  generation += 1
  for (const l of [...listeners]) {
    try { l() } catch { /* one listener never stops another */ }
  }
}

/**
 * The spare preview's deck segment: the generation when the rules name the
 * spare rule, else null (a non-spare preview never re-asks on a deck change,
 * keeping the server's 10/min for edits).
 */
export function previewDeckSegment(rulesKey: string | null): number | null {
  return rulesKey !== null && rulesKeyNamesSpare(rulesKey) ? generation : null
}

/** Whether a canonical rules JSON (useBinders rulesCacheKey) names spare. */
function rulesKeyNamesSpare(rulesKey: string): boolean {
  try {
    return rulesNameSpare(JSON.parse(rulesKey) as { conditions?: { field?: unknown }[] })
  } catch {
    // Unreadable: treat as deck dependent (fail closed).
    return true
  }
}

/** The search filter keys of the spare rule (a standalone spare search). */
const SPARE_FILTER_KEYS = ['spare_min', 'spare_max'] as const

/** Per (collection, user): the spare binder ids, or null when the binders list is not known. */
type SpareIndex = Map<string, Set<number> | null>

function pairKey(collectionId: unknown, userId: unknown): string {
  return [String(collectionId), String(userId)].join(KEY_SEPARATOR)
}

/** The canonical filter JSON's index in every copies list key (useCopies FILTER_KEY_INDEX). */
const FILTER_KEY_INDEX = 3

function namesSpareFilter(f: Record<string, unknown>): boolean {
  return SPARE_FILTER_KEYS.some(k => f[k] !== undefined && f[k] !== null)
}

/** Whether a read scoped by `binder` (and filters `f`) depends on My Decks. */
function dependsOnDecks(binder: unknown, f: Record<string, unknown>, spare: Set<number> | null | undefined): boolean {
  if (namesSpareFilter(f)) return true
  if (typeof binder !== 'number') return false
  // The binders list is not known for this pair: any binder might be a
  // spare one, so every binder-scoped read is dropped (fail closed).
  if (!spare) return true
  return spare.has(binder)
}

function parse(json: unknown): Record<string, unknown> | null {
  try {
    const v = JSON.parse(String(json)) as unknown
    return v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : null
  } catch {
    return null
  }
}

/** A copies list / grouped list key: [copies, collectionId, userId, <canonical filters>, ...]. */
function copiesKeyDepends(key: QueryKey, index: SpareIndex): boolean {
  const f = parse(key[FILTER_KEY_INDEX])
  if (!f) return false
  return dependsOnDecks(f.binder, f, index.get(pairKey(key[1], key[2])))
}

/** A pocket read key: [copies-card, collectionId, userId, 'pocket', <scope JSON>]. */
function pocketKeyDepends(key: QueryKey, index: SpareIndex): boolean {
  // eslint-disable-next-line i18next/no-literal-string -- query key segment (useCopies pocketCopiesQueryKey)
  if (key[3] !== 'pocket') return false
  const scope = parse(key[4])
  if (!scope) return false
  const base = scope.base && typeof scope.base === 'object' ? scope.base as Record<string, unknown> : {}
  return dependsOnDecks(scope.binder, base, index.get(pairKey(key[1], key[2])))
}

/** A binder whose counts depend on My Decks. */
function isSpareBinder(b: Binder): boolean {
  return b.binder_id !== null && rulesNameSpare(b.rules)
}

/**
 * Drop every deck-dependent cache of `queryClient` (see the module comment).
 * Synchronous: by the time it returns, no deck-dependent answer read before
 * the change can land in (or be served from) the cache.
 */
export function applyDeckDependentInvalidation(queryClient: QueryClient): void {
  const cache = queryClient.getQueryCache()
  const index: SpareIndex = new Map()

  // Binders lists: which binders are spare, per (collection, user).
  for (const q of cache.findAll({ queryKey: [QK.binders] })) {
    const data = q.state.data as BindersList | undefined
    const pair = pairKey(q.queryKey[1], q.queryKey[2])
    if (!data) {
      index.set(pair, null)
      // An answer in flight was read before the change: drop it and re-read.
      if (q.state.fetchStatus === 'fetching') void queryClient.resetQueries({ queryKey: q.queryKey, exact: true })
      continue
    }
    const spare = new Set(data.binders.filter(isSpareBinder).map(b => b.binder_id as number))
    index.set(pair, spare)
    if (!spare.size && q.state.fetchStatus !== 'fetching') continue
    if (spare.size) {
      // The spare binders' counts are not current: "checking" until the
      // re-read answers. Every other binder keeps its numbers. Written on the
      // query itself (setState), so a background error state is kept.
      q.setState({ data: withSpare(data, b => ({ ...b, line_count: null, copy_count: null, value_usd: null, stats_pending: true })) })
    }
    // Re-read now, mounted or not: a read in flight (its answer predates the
    // change) is cancelled by the refetch (cancelRefetch) and never lands.
    const key = q.queryKey
    const gen = generation
    // eslint-disable-next-line i18next/no-literal-string -- a TanStack option value
    void queryClient.refetchQueries({ queryKey: key, exact: true, type: 'all' }, { cancelRefetch: true })
      .then(() => settlePending(queryClient, key, gen))
  }

  // Copy lists, grouped lists and pockets of spare binders (or spare filters):
  // reset, so pagination restarts at page 1 and an in-flight answer is dropped.
  void queryClient.resetQueries({ predicate: q => q.queryKey[0] === QK.copies && copiesKeyDepends(q.queryKey, index) })
  void queryClient.resetQueries({ predicate: q => q.queryKey[0] === QK.copiesDetail && pocketKeyDepends(q.queryKey, index) })

  // Spare previews: removed (their key also carries the generation, so a
  // reader re-asks under the new one; an older answer lands nowhere read).
  queryClient.removeQueries({
    predicate: q => q.queryKey[0] === QK.binderPreview && typeof q.queryKey[q.queryKey.length - 1] === 'string'
      && rulesKeyNamesSpare(q.queryKey[q.queryKey.length - 1] as string),
  })
}

/** The list with every spare binder passed through `fn` (the rest untouched). */
function withSpare(list: BindersList, fn: (b: Binder) => Binder): BindersList {
  return { ...list, binders: list.binders.map(b => (isSpareBinder(b) ? fn(b) : b)) }
}

/**
 * The re-read after generation `gen` settled. If it failed (or never
 * replaced the data), the spare binders still marked "checking" become
 * UNAVAILABLE (null counts kept, stats_available false): never a count from
 * before the change, never "checking" forever. Only while `gen` is still
 * the newest generation: a newer change owns the list (and its own re-read).
 * A read still in flight (another refetch joined it) is waited for first.
 */
function settlePending(queryClient: QueryClient, key: QueryKey, gen: number): void {
  if (gen !== generation) return
  const q = queryClient.getQueryCache().find({ queryKey: key, exact: true })
  if (!q) return
  if (q.state.fetchStatus === 'fetching') {
    // eslint-disable-next-line i18next/no-literal-string -- a TanStack option value
    void queryClient.refetchQueries({ queryKey: key, exact: true, type: 'all' }, { cancelRefetch: false })
      .then(() => settlePending(queryClient, key, gen))
    return
  }
  const data = q.state.data as BindersList | undefined
  if (!data || !data.binders.some(b => b.stats_pending)) return
  q.setState({
    data: {
      ...data,
      binders: data.binders.map((b) => {
        if (!b.stats_pending) return b
        const { stats_pending: _pending, ...rest } = b
        void _pending
        return { ...rest, line_count: null, copy_count: null, value_usd: null, stats_available: false }
      }),
    },
  })
}

/**
 * Wire a query client to the signal (App.tsx, once). Returns the unsubscribe.
 */
export function registerDeckDependentQueryClient(queryClient: QueryClient): () => void {
  return subscribeDeckGeneration(() => applyDeckDependentInvalidation(queryClient))
}

// --- Remote reconcile -----------------------------------------------------------
//
// A deck list LOAD (another device, the deck-box runner, another tab) and a
// server row a reconciliation installs (a stale refusal, a lost write
// resolved, a one-row re-read) can bring My Decks changes this tab never
// wrote. The baseline is the last trusted stamp of every deck, per user; a
// row that differs from it invalidates and becomes the new baseline.

/** One deck as far as the spare rule cares: its id, revision and ownership. */
export interface DeckStamp {
  id: string
  rev?: number | null
  ownership?: string | null
  /** Fallback when there is no rev (a row from an older load). */
  savedAt?: string | null
}

/** userId -> deck id -> stamp. No entry for a user = no trusted baseline yet. */
const lastSeen = new Map<string, Map<string, string>>()

function stampOf(d: DeckStamp): string {
  return `${d.rev ?? ''}|${d.ownership ?? ''}|${d.rev === undefined || d.rev === null ? d.savedAt ?? '' : ''}`
}

function baselineOf(decks: readonly DeckStamp[]): Map<string, string> {
  return new Map(decks.map(d => [d.id, stampOf(d)]))
}

function sameBaseline(a: Map<string, string>, b: Map<string, string>): boolean {
  if (a.size !== b.size) return false
  for (const [k, v] of a) if (b.get(k) !== v) return false
  return true
}

/**
 * A complete deck list for `userId` was installed from the server. It
 * becomes the baseline. When it differs from the previous one, My Decks
 * changed remotely: invalidate. The FIRST list this tab sees for the user
 * also invalidates: deck-dependent data may already be cached (the
 * collection was opened before any deck list loaded) and nothing tells it
 * apart from data read under older decks. Returns whether it invalidated.
 */
export function noteSavedDecksLoaded(userId: string, decks: readonly DeckStamp[]): boolean {
  const next = baselineOf(decks)
  const prev = lastSeen.get(userId)
  lastSeen.set(userId, next)
  if (prev !== undefined && sameBaseline(prev, next)) return false
  invalidateDeckDependentCollectionData()
  return true
}

/**
 * This tab's own write landed: record the list as it now stands, so the
 * next load (which reads the same rows) does not invalidate a second time.
 */
export function noteSavedDecksWritten(userId: string, decks: readonly DeckStamp[]): void {
  lastSeen.set(userId, baselineOf(decks))
}

/**
 * A reconciliation installed the server's row for deck `id` (null: the row
 * is gone). When it differs from the trusted stamp (or there is no trusted
 * baseline yet), My Decks changed: invalidate and trust the row. Returns
 * whether it invalidated.
 */
export function noteServerDeckRow(userId: string, id: string, row: DeckStamp | null): boolean {
  const base = lastSeen.get(userId)
  const next = row ? stampOf(row) : null
  if (base) {
    if ((base.get(id) ?? null) === next) return false
    if (next === null) base.delete(id)
    else base.set(id, next)
  }
  invalidateDeckDependentCollectionData()
  return true
}

/** Test seam: forget the per-user baselines and the generation. */
export function __resetDeckDependentState(): void {
  lastSeen.clear()
  generation = 0
}
